/*
 * Назначение: корневой компонент админки.
 * Функционал: провайдеры (TanStack Query, тема, авторизация, проект),
 * декларация маршрутов через react-router. Каждая страница имеет свой URL,
 * приватная зона защищена RequireAuth, гостевой /login недоступен залогиненному.
 */

import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";

import { AuthProvider, useAuth } from "./components/auth/auth-provider";
import { AdminLayout } from "./components/layout/admin-layout";
import { ProjectProvider } from "./components/project/project-provider";
import { ThemeProvider, useTheme } from "./components/theme/theme-provider";
import { AnalyticsPage } from "./pages/analytics";
import { DashboardPage } from "./pages/dashboard";
import { DialogsPage } from "./pages/dialogs";
import { IntegrationsPage } from "./pages/integrations";
import { KnowledgeBasePage } from "./pages/knowledge-base";
import { LeadsPage } from "./pages/leads";
import { LoginPage } from "./pages/login";
import { NotFoundPage } from "./pages/not-found";
import { ProfilePage } from "./pages/profile";
import { ProjectWizardPage } from "./pages/project-wizard";
import { SecurityPage } from "./pages/security/security";
import { SettingsPage } from "./pages/settings";
import { UsersPage } from "./pages/users";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

/** Хост для Toaster — нужен для подхвата текущей темы. */
function GlobalToaster() {
  const { resolvedTheme } = useTheme();
  return <Toaster theme={resolvedTheme} richColors closeButton position="top-right" />;
}

/** Гость не должен видеть приватную зону — отправляем на /login, запоминаем исходный URL. */
function RequireAuth() {
  const { status } = useAuth();
  const location = useLocation();

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        Загрузка…
      </div>
    );
  }

  if (status === "anonymous") {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return (
    <ProjectProvider>
      <AdminLayout>
        <Outlet />
      </AdminLayout>
    </ProjectProvider>
  );
}

/** Залогиненный пользователь не должен видеть /login — кидаем на /dashboard. */
function GuestOnly() {
  const { status } = useAuth();

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        Загрузка…
      </div>
    );
  }

  if (status === "authenticated") {
    return <Navigate to="/dashboard" replace />;
  }

  return <LoginPage />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              {/* Гостевые маршруты */}
              <Route path="/login" element={<GuestOnly />} />

              {/* Приватная зона */}
              <Route element={<RequireAuth />}>
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<DashboardPage />} />

                <Route path="profile" element={<ProfilePage />} />

                <Route path="projects/new" element={<ProjectWizardPage />} />

                <Route path="knowledge-base" element={<KnowledgeBasePage />} />
                <Route
                  path="knowledge-base/:tab"
                  element={<KnowledgeBasePage />}
                />

                <Route path="dialogs" element={<DialogsPage />} />
                <Route path="dialogs/:dialogId" element={<DialogsPage />} />

                <Route path="leads" element={<LeadsPage />} />
                <Route
                  path="leads/:leadId"
                  element={<Navigate to="/leads" replace />}
                />

                <Route path="settings" element={<SettingsPage />} />
                <Route path="settings/:tab" element={<SettingsPage />} />
                <Route path="analytics" element={<AnalyticsPage />} />
                <Route path="integrations" element={<IntegrationsPage />} />
                <Route path="security" element={<SecurityPage />} />
                <Route path="users" element={<UsersPage />} />
              </Route>

              {/* 404 */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
        <GlobalToaster />
      </ThemeProvider>
    </QueryClientProvider>
  );
}
